import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

export interface LifecycleStatusReasonCategory {
  lifecycle_status_reason_category_id: number;
  lifecycle_status_reason_category_key: string;
  lifecycle_status_reason_category_display_name: string;
}

export interface LifecycleStatusReason {
  lifecycle_status_reason_id?: number;
  reason_name: string;
  is_active: boolean;
  tenant_id: number;
  lifecycle_status_reason_category_id: number;
  created_by?: number | null;
  updated_by?: number | null;
}

export interface PaginatedCategoryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: LifecycleStatusReasonCategory[];
}

export interface PaginatedReasonResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: LifecycleStatusReason[];
}

const getUserId = () => {
  const authResponse = localStorage.getItem("authResponse");
  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);
      return auth.user_id;
    } catch (e) {
      console.warn("Could not parse auth response for user_id");
    }
  }
  return null;
};

class LifecycleStatusReasonServiceClass {
  async getCategories(): Promise<LifecycleStatusReasonCategory[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/lifecycle-status-reason-category/`, {
        headers: getAuthHeaders(),
      });
      return response.data.results || response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to fetch categories");
    }
  }

  async getReasons(tenantId: number): Promise<LifecycleStatusReason[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/lifecycle-status-reason/?tenant_id=${tenantId}`, {
        headers: getAuthHeaders(),
      });
      return response.data.results || response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to fetch reasons");
    }
  }

  async createReason(data: Partial<LifecycleStatusReason>): Promise<LifecycleStatusReason> {
    try {
      const payload = {
        ...data,
        created_by: getUserId(),
        updated_by: getUserId(),
      };
      const response = await axios.post(`${API_BASE_URL}/lifecycle-status-reason/`, payload, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to create reason");
    }
  }

  async updateReason(id: number, data: Partial<LifecycleStatusReason>, tenantId: number): Promise<LifecycleStatusReason> {
    try {
      const payload = {
        ...data,
        tenant_id: tenantId,
        updated_by: getUserId(),
      };
      const response = await axios.patch(`${API_BASE_URL}/lifecycle-status-reason/${id}/`, payload, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to update reason");
    }
  }

  async deleteReason(id: number): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/lifecycle-status-reason/${id}/`, {
        headers: getAuthHeaders(),
      });
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to delete reason");
    }
  }
}

export const LifecycleStatusReasonService = new LifecycleStatusReasonServiceClass();
